import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import Grid from "@material-ui/core/Grid";
import Toolbar from "@material-ui/core/Toolbar";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";

import { InputButton } from "../../components/input-button/InputButton";
import { MenuItem } from "../../components/menu_item/MenuItem";
import { MyAccount } from "../../components/my_account/MyAccount";
import { BranchManagerService } from "../../services/BranchManagerService";
import { BranchManagerDashboardScreen } from "../branch_manager_dashboard_screen/BranchManagerDashboardScreen";
import { Branch } from "./components/branch/Branch";
import { useAuthenticationContext } from "../../context/UserContext";

enum Views {
  MyAccount = "MyAccount",
  Branch = "Branch",
}

const useStyles = makeStyles((theme) => ({
  topRoot: {
    backgroundColor: theme.palette.background.default,
    minHeight: "100%",
    overflow: "hidden",
    width: "100%",
    flexGrow: 1,
  },
  title: {
    flexGrow: 1,
  },
  logo: {
    widht: 43,
    height: 30,
    marginRight: 10,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 40,
    marginRight: 5,
  },
  appBar: {
    backgroundColor: theme.palette.background.default,
  },
  menuButton: {
    textTransform: "none",
  },
  root: {
    backgroundColor: theme.palette.grey[200],
    display: "flex",
    minHeight: "100vh",
    overflow: "hidden",
    width: "100%",
    paddingTop: 90,
    paddingBottom: 15,
  },
  leftSide: {
    height: "100%",
    overflow: "hidden",
    flexGrow: 1,
  },
  rightSide: {
    height: "100%",
    overflow: "hidden",
    paddingRight: 20,
    flexGrow: 1,
  },
  leftMenuSectionTitle: {
    color: "#818189",
    fontSize: 16,
    fontWeight: 700,
    marginLeft: 20,
  },
  leftMenuButton: {
    textTransform: "none",
    paddingLeft: 30,
  },
  leftMenuButtonSelected: {
    textTransform: "none",
    paddingLeft: 30,
    borderLeftWidth: 7,
    borderLeftColor: theme.palette.primary.dark,
    borderLeftStyle: "solid",
    borderRadius: 0,
  },
  leftMenuItemLabel: {
    fontWeight: 400,
    fontSize: 16,
    color: theme.palette.primary.dark,
  },
  leftMenuItemLabelSelected: {
    fontWeight: 700,
    fontSize: 16,
    color: theme.palette.primary.dark,
  },
  spacer: {
    width: "100%",
    height: 30,
  },
  addNewBranch: {
    textTransform: "none",
    marginLeft: 20,
  },
}));

export const BranchManagerAccountScreen = (props: any) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [user] = useAuthenticationContext();
  const userAvatar = user.avatar();
  const userName = user.fullName();
  const userId = user.id;
  const [view, setView] = useState(Views.MyAccount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [account, setAccount] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const loadAccountAndBranches = async () => {
    setLoading(true);
    setError(false);
    setAccount(null);
    setBranches([]);
    setSelectedBranchId(null);
    try {
      const accountAndBranches = await BranchManagerService.getAccountAndBranches(
        {
          id: userId,
        }
      );
      if (
        accountAndBranches.isSuccess &&
        accountAndBranches.data.code === 100
      ) {
        const responseData = JSON.parse(
          JSON.stringify(accountAndBranches.data.data)
        );
        setAccount(responseData.account);
        setBranches(responseData.branches);
      } else {
        setError(true);
      }
    } catch (_) {
      setError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAccountAndBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const myAccountClick = (_: React.MouseEvent<HTMLButtonElement>) =>
    setView(Views.MyAccount);

  const selectBranch = (branchId: number) => {
    setView(Views.Branch);
    setSelectedBranchId(branchId);
  };

  const addBranch = () => {}; //ToDo add navigation to create branch screen

  return (
    <>
      <div className={classes.topRoot}>
        <Grid container>
          <Grid item xs={12} sm={12} lg={12}>
            <AppBar className={classes.appBar} position="fixed" elevation={5}>
              <Toolbar variant="dense">
                <Link to={BranchManagerDashboardScreen.routeName}>
                  <img
                    className={classes.logo}
                    alt="logo"
                    src={process.env.PUBLIC_URL + "/logo_blue.png"}
                  />
                </Link>
                <Typography className={classes.title} />
                <Button className={classes.menuButton} onClick={null}>
                  <img
                    className={classes.avatar}
                    alt="user-avatar"
                    src={userAvatar}
                  />
                  {userName}
                  <ArrowDropDownIcon />
                </Button>
              </Toolbar>
            </AppBar>
          </Grid>
          <Grid item xs={12} sm={12} lg={12}>
            <div className={classes.root}>
              <Grid container>
                <Grid item xs={12} sm={12} lg={12}>
                  {loading && <CircularProgress size={30} />}
                  {error && (
                    <Alert
                      severity="error"
                      action={
                        <Button
                          onClick={loadAccountAndBranches}
                          color="inherit"
                          size="small"
                        >
                          <FormattedMessage id="sharedScreen.loadAgain" />
                        </Button>
                      }
                    >
                      <FormattedMessage id="sharedScreen.thereWasAProblemLoadingDatas" />
                    </Alert>
                  )}
                </Grid>
                <Grid
                  className={classes.leftSide}
                  item
                  container
                  xs={2}
                  sm={2}
                  lg={2}
                >
                  <Grid item xs={12} sm={12} lg={12}>
                    <Typography
                      className={classes.leftMenuSectionTitle}
                      variant="h4"
                      component="h4"
                    >
                      <FormattedMessage id="sharedScreen.personal" />
                    </Typography>
                    <MenuItem
                      selected={view === Views.MyAccount ?? false}
                      callback={myAccountClick}
                      label={formatMessage({ id: "sharedScreen.myAccount" })}
                    />
                    <div className={classes.spacer} />
                    <Typography
                      className={classes.leftMenuSectionTitle}
                      variant="h4"
                      component="h4"
                    >
                      <FormattedMessage id="sharedScreen.branches" />
                    </Typography>
                    {branches.map((branch) => (
                      <Grid>
                        <MenuItem
                          selected={
                            view === Views.Branch &&
                            branch.id === selectedBranchId
                          }
                          callback={(_) => selectBranch(branch.id)}
                          label={branch.name}
                        />
                      </Grid>
                    ))}
                    <div className={classes.spacer} />
                    <InputButton
                      className={classes.addNewBranch}
                      onClick={addBranch}
                      color="secondary"
                      variant="outlined"
                    >
                      + <FormattedMessage id="sharedScreen.addNewBranch" />
                    </InputButton>
                  </Grid>
                </Grid>
                <Grid
                  className={classes.rightSide}
                  item
                  container
                  xs={10}
                  sm={10}
                  lg={10}
                >
                  {view === Views.MyAccount && account !== null && (
                    <MyAccount {...account} />
                  )}
                  {view === Views.Branch && (
                    <>
                      {branches.map((branch) =>
                        selectedBranchId === branch.id ? (
                          <Branch {...branch} />
                        ) : (
                          <></>
                        )
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

BranchManagerAccountScreen.routeName = "/branch-manager-account";
